import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
  Linking,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackParamList, RootStackNavigationProp } from '../../../../types/navigation';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('CasketUrnInputUrn');

import {
  UrnDetails,
  CasketUrnDetails,
  casketUrnInfoService
} from '../../../../services/casketUrnInfo';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type CasketUrnInputUrnScreenRouteProp = RouteProp<RootStackParamList, 'CasketUrnInputUrn'>;

type UrnType = 'metal' | 'ceramic' | 'wood' | 'biodegradable' | 'glass' | null;

export const CasketUrnInputUrnScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<CasketUrnInputUrnScreenRouteProp>();

  const { casketUrnData, isEditing } = route.params || {};

  const [selectedUrn, setSelectedUrn] = useState<UrnType>(
    casketUrnData?.specificType as UrnType || null
  );
  const [specialInstructions, setSpecialInstructions] = useState(
    casketUrnData?.specialInstructions || ''
  );

  // Initialize details with existing data if editing
  const getInitialDetails = () => {
    if (casketUrnData?.details && casketUrnData.containerType === 'urn') {
      return casketUrnData.details as UrnDetails;
    }
    return {};
  };

  const initialDetails = getInitialDetails();

  // Metal Urn Details
  const [metalDetails, setMetalDetails] = useState<UrnDetails>({
    material: initialDetails?.material || '',
    size: initialDetails?.size || '',
    design: initialDetails?.design || '',
    color: initialDetails?.color || '',
    personalization: initialDetails?.personalization || '',
    specifications: initialDetails?.specifications || '',
  });

  // Ceramic Urn Details
  const [ceramicDetails, setCeramicDetails] = useState<UrnDetails>({
    material: initialDetails?.material || '',
    size: initialDetails?.size || '',
    design: initialDetails?.design || '',
    color: initialDetails?.color || '',
    personalization: initialDetails?.personalization || '',
    specifications: initialDetails?.specifications || '',
  });

  // Wood Urn Details
  const [woodDetails, setWoodDetails] = useState<UrnDetails>({
    material: initialDetails?.material || '',
    size: initialDetails?.size || '',
    design: initialDetails?.design || '',
    color: initialDetails?.color || '',
    personalization: initialDetails?.personalization || '',
    specifications: initialDetails?.specifications || '',
  });

  // Biodegradable Urn Details
  const [biodegradableDetails, setBiodegradableDetails] = useState<UrnDetails>({
    material: initialDetails?.material || '',
    size: initialDetails?.size || '',
    design: initialDetails?.design || '',
    color: initialDetails?.color || '',
    personalization: initialDetails?.personalization || '',
    specifications: initialDetails?.specifications || '',
  });

  // Glass/Other Urn Details
  const [glassDetails, setGlassDetails] = useState<UrnDetails>({
    material: initialDetails?.material || '',
    size: initialDetails?.size || '',
    design: initialDetails?.design || '',
    color: initialDetails?.color || '',
    personalization: initialDetails?.personalization || '',
    specifications: initialDetails?.specifications || '',
  });

  const handleSave = async () => {
    try {
      // Determine which urn details to save based on selected type
      let urnDetails: CasketUrnDetails = null;

      switch (selectedUrn) {
        case 'metal':
          urnDetails = metalDetails;
          break;
        case 'ceramic':
          urnDetails = ceramicDetails;
          break;
        case 'wood':
          urnDetails = woodDetails;
          break;
        case 'biodegradable':
          urnDetails = biodegradableDetails;
          break;
        case 'glass':
          urnDetails = glassDetails;
          break;
      }

      // Prepare the data to save
      const casketUrnUpdateData = {
        containerType: 'urn',
        specificType: selectedUrn || '',
        specialInstructions: specialInstructions,
        details: urnDetails,
        notes: specialInstructions, // Map specialInstructions to notes field
      };

      let savedCasketUrn;

      if (isEditing && casketUrnData?.id) {
        // Update existing record
        savedCasketUrn = await casketUrnInfoService.updateCasketUrnInfo(
          casketUrnData.id,
          casketUrnUpdateData
        );
        logger.debug('Urn information updated successfully with ID:', savedCasketUrn.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        (navigation as any).navigate('CasketUrnLockUnlock', { casketUrnId: savedCasketUrn.id });
      } else {
        // Create new record
        savedCasketUrn = await casketUrnInfoService.createCasketUrnInfo(casketUrnUpdateData);
        logger.debug('Urn information saved successfully with ID:', savedCasketUrn.id);

        // Navigate to lock/unlock screen with the ID
        (navigation as any).navigate('CasketUrnLockUnlock', { casketUrnId: savedCasketUrn.id });
      }
    } catch (error) {
      logger.error('Error saving urn information:', error);
    }
  };

  const handleLinkPress = () => {
    Linking.openURL('https://titancasket.com/collections/cremation-urns');
  };

  const renderDetailsInput = () => {
    switch (selectedUrn) {
      case 'metal':
        return (
          <View>
            <Text style={styles.detailsTitle}>Metal Urn Details:</Text>

            {/* Material Input */}
            <Text style={styles.inputLabel}>Material type (brass, bronze, pewter, stainless steel):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify the type of metal..."
              value={metalDetails.material}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, material: value })
              }
              multiline
            />

            {/* Size Input */}
            <Text style={styles.inputLabel}>Size preference (individual, companion, or keepsake):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your size requirements..."
              value={metalDetails.size}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, size: value })
              }
              multiline
            />

            {/* Design Input */}
            <Text style={styles.inputLabel}>Design style (traditional, modern, or artistic):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify your preferred design style..."
              value={metalDetails.design}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, design: value })
              }
              multiline
            />

            {/* Color Input */}
            <Text style={styles.inputLabel}>Color and finish options:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your preferred color and finish..."
              value={metalDetails.color}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, color: value })
              }
              multiline
            />

            {/* Personalization Input */}
            <Text style={styles.inputLabel}>Personalization (engraving or custom designs):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any engravings or custom designs..."
              value={metalDetails.personalization}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, personalization: value })
              }
              multiline
            />

            {/* Additional Specifications */}
            <Text style={styles.inputLabel}>Additional specifications:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Any other specific requirements or preferences..."
              value={metalDetails.specifications}
              onChangeText={(value) =>
                setMetalDetails({ ...metalDetails, specifications: value })
              }
              multiline
            />

            <Text style={[styles.detailsTitle, { marginTop: 16 }]}>Buy your urn ahead of time:</Text>
            <TouchableOpacity onPress={handleLinkPress}>
              <Text style={styles.linkText}>Click HERE for our recommended selection.</Text>
            </TouchableOpacity>
          </View>
        );
      case 'ceramic':
        return (
          <View>
            <Text style={styles.detailsTitle}>Ceramic Urn Details:</Text>

            {/* Material Input */}
            <Text style={styles.inputLabel}>Ceramic type (porcelain, stoneware, or earthenware):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify the type of ceramic..."
              value={ceramicDetails.material}
              onChangeText={(value) =>
                setCeramicDetails({ ...ceramicDetails, material: value })
              }
              multiline
            />

            {/* Size Input */}
            <Text style={styles.inputLabel}>Size options (individual or keepsake):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your size requirements..."
              value={ceramicDetails.size}
              onChangeText={(value) =>
                setCeramicDetails({ ...ceramicDetails, size: value })
              }
              multiline
            />

            {/* Design Input */}
            <Text style={styles.inputLabel}>Glazing and decorative preferences:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe glazing, colors, and decorative elements..."
              value={ceramicDetails.design}
              onChangeText={(value) =>
                setCeramicDetails({ ...ceramicDetails, design: value })
              }
              multiline
            />

            {/* Color Input */}
            <Text style={styles.inputLabel}>Color preferences:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your preferred colors and patterns..."
              value={ceramicDetails.color}
              onChangeText={(value) =>
                setCeramicDetails({ ...ceramicDetails, color: value })
              }
              multiline
            />

            {/* Personalization Input */}
            <Text style={styles.inputLabel}>Handcrafted vs manufactured preferences:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify if you prefer handcrafted or manufactured urns..."
              value={ceramicDetails.personalization}
              onChangeText={(value) =>
                setCeramicDetails({ ...ceramicDetails, personalization: value })
              }
              multiline
            />

            {/* Additional Specifications */}
            <Text style={styles.inputLabel}>Additional specifications:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Any other specific requirements or preferences..."
              value={ceramicDetails.specifications}
              onChangeText={(value) =>
                setCeramicDetails({ ...ceramicDetails, specifications: value })
              }
              multiline
            />

            <Text style={[styles.detailsTitle, { marginTop: 16 }]}>Buy your urn ahead of time:</Text>
            <TouchableOpacity onPress={handleLinkPress}>
              <Text style={styles.linkText}>Click HERE for our recommended selection.</Text>
            </TouchableOpacity>
          </View>
        );
      case 'wood':
        return (
          <View>
            <Text style={styles.detailsTitle}>Wood Urn Details:</Text>

            {/* Material Input */}
            <Text style={styles.inputLabel}>Wood type (oak, maple, cherry, walnut):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify the type of wood..."
              value={woodDetails.material}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, material: value })
              }
              multiline
            />

            {/* Size Input */}
            <Text style={styles.inputLabel}>Size and capacity needs:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your size and capacity requirements..."
              value={woodDetails.size}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, size: value })
              }
              multiline
            />

            {/* Design Input */}
            <Text style={styles.inputLabel}>Design elements (inlays, carvings):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any inlays, carvings, or design elements..."
              value={woodDetails.design}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, design: value })
              }
              multiline
            />

            {/* Color Input */}
            <Text style={styles.inputLabel}>Finish preferences (natural, stained, or polished):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your preferred finish..."
              value={woodDetails.color}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, color: value })
              }
              multiline
            />

            {/* Personalization Input */}
            <Text style={styles.inputLabel}>Custom engraving options:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any custom engraving or personalization..."
              value={woodDetails.personalization}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, personalization: value })
              }
              multiline
            />

            {/* Additional Specifications */}
            <Text style={styles.inputLabel}>Additional specifications:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Any other specific requirements or preferences..."
              value={woodDetails.specifications}
              onChangeText={(value) =>
                setWoodDetails({ ...woodDetails, specifications: value })
              }
              multiline
            />

            <Text style={[styles.detailsTitle, { marginTop: 16 }]}>Buy your urn ahead of time:</Text>
            <TouchableOpacity onPress={handleLinkPress}>
              <Text style={styles.linkText}>Click HERE for our recommended selection.</Text>
            </TouchableOpacity>
          </View>
        );
      case 'biodegradable':
        return (
          <View>
            <Text style={styles.detailsTitle}>Biodegradable Urn Details:</Text>

            {/* Material Input */}
            <Text style={styles.inputLabel}>Material type (paper, salt, sand, or natural fibers):</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify the biodegradable material..."
              value={biodegradableDetails.material}
              onChangeText={(value) =>
                setBiodegradableDetails({ ...biodegradableDetails, material: value })
              }
              multiline
            />

            {/* Size Input */}
            <Text style={styles.inputLabel}>Size and ceremony requirements:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe size needs and ceremony type..."
              value={biodegradableDetails.size}
              onChangeText={(value) =>
                setBiodegradableDetails({ ...biodegradableDetails, size: value })
              }
              multiline
            />

            {/* Design Input */}
            <Text style={styles.inputLabel}>Water-soluble options for water ceremonies:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify if you need water-soluble options..."
              value={biodegradableDetails.design}
              onChangeText={(value) =>
                setBiodegradableDetails({ ...biodegradableDetails, design: value })
              }
              multiline
            />

            {/* Color Input */}
            <Text style={styles.inputLabel}>Eco-friendly decorative elements:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any eco-friendly decorations..."
              value={biodegradableDetails.color}
              onChangeText={(value) =>
                setBiodegradableDetails({ ...biodegradableDetails, color: value })
              }
              multiline
            />

            {/* Personalization Input */}
            <Text style={styles.inputLabel}>Tree planting urns with growth medium:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify if you want tree planting capabilities..."
              value={biodegradableDetails.personalization}
              onChangeText={(value) =>
                setBiodegradableDetails({ ...biodegradableDetails, personalization: value })
              }
              multiline
            />

            {/* Additional Specifications */}
            <Text style={styles.inputLabel}>Time to decomposition preferences:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify decomposition timeline requirements..."
              value={biodegradableDetails.specifications}
              onChangeText={(value) =>
                setBiodegradableDetails({ ...biodegradableDetails, specifications: value })
              }
              multiline
            />

            <Text style={[styles.detailsTitle, { marginTop: 16 }]}>Buy your urn ahead of time:</Text>
            <TouchableOpacity onPress={handleLinkPress}>
              <Text style={styles.linkText}>Click HERE for our recommended selection.</Text>
            </TouchableOpacity>
          </View>
        );
      case 'glass':
        return (
          <View>
            <Text style={styles.detailsTitle}>Glass/Art Urn Details:</Text>

            {/* Material Input */}
            <Text style={styles.inputLabel}>Glass or art material type:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Hand-blown glass, crystal, mixed media, etc..."
              value={glassDetails.material}
              onChangeText={(value) =>
                setGlassDetails({ ...glassDetails, material: value })
              }
              multiline
            />

            {/* Size Input */}
            <Text style={styles.inputLabel}>Size and display considerations:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe size and how it will be displayed..."
              value={glassDetails.size}
              onChangeText={(value) =>
                setGlassDetails({ ...glassDetails, size: value })
              }
              multiline
            />

            {/* Design Input */}
            <Text style={styles.inputLabel}>Artistic design preferences:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Unique patterns, themed designs, custom art..."
              value={glassDetails.design}
              onChangeText={(value) =>
                setGlassDetails({ ...glassDetails, design: value })
              }
              multiline
            />

            {/* Color Input */}
            <Text style={styles.inputLabel}>Color and visual preferences:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe preferred colors and visual elements..."
              value={glassDetails.color}
              onChangeText={(value) =>
                setGlassDetails({ ...glassDetails, color: value })
              }
              multiline
            />

            {/* Personalization Input */}
            <Text style={styles.inputLabel}>Themed or specialty options:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Sports, hobbies, jewelry urns, scattering tubes..."
              value={glassDetails.personalization}
              onChangeText={(value) =>
                setGlassDetails({ ...glassDetails, personalization: value })
              }
              multiline
            />

            {/* Additional Specifications */}
            <Text style={styles.inputLabel}>Commission or custom requirements:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Custom art pieces, memorial sculptures, special features..."
              value={glassDetails.specifications}
              onChangeText={(value) =>
                setGlassDetails({ ...glassDetails, specifications: value })
              }
              multiline
            />

            <Text style={[styles.detailsTitle, { marginTop: 16 }]}>Buy your urn ahead of time:</Text>
            <TouchableOpacity onPress={handleLinkPress}>
              <Text style={styles.linkText}>Click HERE for our recommended selection.</Text>
            </TouchableOpacity>
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          What type of urn would you prefer for your ashes?
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedUrn('metal')}
            >
              <View style={styles.radioButton}>
                {selectedUrn === 'metal' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Metal Urn</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedUrn('ceramic')}
            >
              <View style={styles.radioButton}>
                {selectedUrn === 'ceramic' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Ceramic Urn</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedUrn('wood')}
            >
              <View style={styles.radioButton}>
                {selectedUrn === 'wood' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Wood Urn</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedUrn('biodegradable')}
            >
              <View style={styles.radioButton}>
                {selectedUrn === 'biodegradable' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Biodegradable Urn</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedUrn('glass')}
            >
              <View style={styles.radioButton}>
                {selectedUrn === 'glass' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Glass/Art Urn Options</Text>
            </TouchableOpacity>
          </View>

          {renderDetailsInput()}

          <Text style={styles.label}>Special instructions:</Text>
          <TextInput
            style={styles.specialInstructionsInput}
            placeholder="Enter any special instructions..."
            value={specialInstructions}
            onChangeText={setSpecialInstructions}
            multiline
          />

          <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
            <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(100),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
        position: 'absolute',
        width: '85%',
        height: '85%',
        opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  radioGroup: {
    marginBottom: ms(20),
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(20),
  },
  radioButton: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#1C2C4F',
    marginRight: ms(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    width: s(12),
    height: s(12),
    borderRadius: ms(6),
    backgroundColor: '#1C2C4F',
  },
  radioText: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
  },
  structuredInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(15),
    minHeight: s(80),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  inputLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
    marginTop: ms(5),
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  specialInstructionsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(150),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  detailsTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  bottomPadding: {
    height: s(120),
  },
  linkText: {
    color: '#29BFC0',
    textDecorationLine: 'underline',
    marginLeft: ms(16),
    marginBottom: ms(20),
  },
});

export default CasketUrnInputUrnScreen;
